import {AfterContentInit, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {ListEntity} from '../../entities/list.entity';
import {WelcomeList} from '../../interfaces/welcome-list/welcome-list.interface';
import {
  Content, Modal, ModalController,
  NavController, Refresher, ToastController,
} from 'ionic-angular';
import {LoginFormComponent} from '../../../user/login/components/form/login/login-form.component';
import {WindowService} from '../../../shared/services/window/window.service';

/**
 * Abstract class the other listing components will extend
 * @class
 * @implements {AfterContentInit}
 *
 * @description Defines paging, search, infinite scroll, and refresh capabilities
 *
 * # Inputs
 * |name|type|description|
 * |---|---|---|
 * |items|Array|list of items on the listing page|
 */
export abstract class AbstractListComponent implements AfterContentInit {
  /**
   * List of items to be displayed
   * @type {Array}
   */
  @Input()
  items: ListEntity[] = [];

  /**
   * string containing the name of the namespaced window scroll event
   * @type {string}
   */
  @Input()
  scrollEvent: string;

  /**
   * Output for calling component
   * @type {EventEmitter}
   */
  @Output()
  onSelect: EventEmitter<object> = new EventEmitter();

  @Output()
  onAuthError: EventEmitter<object> = new EventEmitter();

  /**
   * Gets the refresher element and casts it as the Refresher component
   * @type {Refresher}
   */
  @ViewChild('refresher', {read: Refresher})
  refresher: Refresher;

  /**
   * Gets the content element and casts it as the Content component
   * @type {Content}
   */
  @ViewChild('content')
  content: Content;

  /**
   * classes for the ion-content element
   * @type {{searchBarOpen: boolean; searchBarClosed: boolean}}
   */
  contentClass = {searchBarOpen: false, searchBarClosed: true};

  /**
   * Property to tell if the component is initting
   * @type {boolean}
   */
  initting: boolean;

  /**
   * Which page we're on
   * @type {number}
   * @protected
   */
  protected page = 0;

  /**
   * Limit for the pages
   * @protected
   * @type {number}
   */
  protected limit: number;

  /**
   * Modal used for user login
   * @type {Modal}
   */
  private loginModal: Modal;

  /**
   * Is a page currently loading
   * @type {boolean}
   */
  private pageLoading: boolean = false;

  /**
   * Scroll-height of the list element on last scroll event (defaults low to be set on first scroll)
   * @type {number}
   */
  private prevListHeight: number = 0;

  /**
   * position of the bottom of the list element on last scroll event (defaults high to be set on first scroll)
   * @type {number}
   */
  private prevScrollPosition: number = 10000;

  /**
   * list div query selector
   * @type {string}
   */
  private listQuerySelector: string = '[data-role=advtech-list]';

  /**
   * @constructor
   * @param {WelcomeList} service service that implements the listing list interface
   * @param {NavController} navCtrl instance of the NavController
   * @param {ModalController} modalCtrl instance of the ModalController
   * @param {ToastController} toast instance of the ToastController
   * @param {WindowService} windowService instance of the WindowService
   */
  constructor(protected service: WelcomeList,
              public navCtrl: NavController,
              public modalCtrl: ModalController,
              public toast: ToastController,
              public windowService: WindowService) {
  }

  /**
   * Gets the list items array
   * @returns {ListEntity[]}
   */
  getItems() {
    return this.items;
  }

  /** @inheritdoc */
  ngAfterContentInit() {
    this.initting = true;
    this.service.setKeyword('');
    this.service.setOffset(this.page * this.service.getLimit());
    this.service.getList().subscribe(
      (res) => this.populateList(res),
      (err) => this.handleServiceError(err),
      () => this.initting = false,
    );
  }

  /**
   * Adds the handler for the window scroll event
   * @returns {void}
   */
  addScrollEvent(): void {
    this.windowService.window.on(this.scrollEvent, (evt: any) => this.hScroll(), true);
  }

  /**
   * Handles the scroll, if at the bottom of the page, gets the next page of the results
   * @returns {void}
   */
  hScroll(): void {
    const listContainer = document.querySelector(this.listQuerySelector),
      listRect = listContainer.getBoundingClientRect(),
      bottomOfList = listRect.bottom,
      listHeight = bottomOfList - listRect.top,
      viewportHeight = document.documentElement.clientHeight;
    if (!this.pageLoading && listHeight >= this.prevListHeight && bottomOfList < this.prevScrollPosition && bottomOfList - 80 < viewportHeight) {
      this.doNextPage();
    }
    this.prevListHeight = listHeight;
    this.prevScrollPosition = bottomOfList;
  }

  /**
   * Handles the scroll
   * @returns {Observable<any>}
   */
  doNextPage(): Observable<any> {
    this.service.setOffset(this.page * this.service.getLimit());
    const observable = this.service.getList(true);
    this.pageLoading = true;

    observable.subscribe(
      (res) => {
        if (res.result.data.length === 0) {
          this.toast.create({
            closeButtonText: 'Ok',
            dismissOnPageChange: true,
            message: 'No more items to retrieve',
            position: 'bottom',
            showCloseButton: true,
          }).present();
        }
        else {
          this.append(this.service.populateListEntities(res.result.data));
          setTimeout(() => {
            this.content._scrollContent.nativeElement.scrollTop += 10;
          }, 50);
        }
      },
      (err) => {
        if (!this.handleServiceError(err)) {
          this.toast.create({
            closeButtonText: 'Ok',
            dismissOnPageChange: true,
            message: 'Could not load more data',
            position: 'bottom',
            showCloseButton: true,
          });
        }
      },
      ()    => this.pageLoading = false,
    );

    return observable;
  }

  /**
   * Handles resetting the list triggered by the refresher
   * @param {boolean} skipLoading should skip loading
   * @returns {Observable<any>}
   */
  doFirstPage(skipLoading = true): Observable<any> {
    this.service.setOffset(0);
    const observable = this.service.getList(skipLoading);
    this.pageLoading = true;

    observable.subscribe(
      (res) => this.reset(res),
      (err) => this.handleServiceError(err),
      ()    => this.pageLoading = false,
    );

    return observable;
  }

  /**
   * Sends the user to the edit page
   * @param {{page, data}} obj the page to navigate to and the data to pass along
   * @returns {Promise}
   */
  doEditPage(obj: ListEntity): Promise<any> {
    return this.navCtrl.push(`${obj.type}Page`, obj);
  }

  /**
   * Sends the user to the create page
   * @param {{page, data}} obj the page to navigate to and the data to pass along
   * @returns {Promise}
   */
  doCreatePage(obj: { page, data }): Promise<any> {
    return this.navCtrl.push(obj.page, obj.data);
  }

  /**
   * Shows the Login modal
   * @returns {Promise<any>}
   */
  showLoginModal(): Promise<any> {
    this.refresher.complete();

    if (!this.loginModal) {
      this.loginModal = this.modalCtrl.create(LoginFormComponent, {
        modal: true,
      });

      this.loginModal.onDidDismiss(() => {
        this.loginModal = null;
        this.doFirstPage();
      });

      return this.loginModal.present();
    }
  }

  /**
   * Handles searching
   * @param {string} keyword term for the search
   * @returns {void}
   */
  hSearch(keyword: string): void {
    this.service.setKeyword(keyword);
    this.doFirstPage(false);
  }

  /**
   * Handles Refresher
   * @param {Refresher} refresher The ionic refresher component
   * @returns {void}
   */
  doRefresh(refresher: Refresher): void {
    const observable = this.doFirstPage();
    observable.subscribe(
      (res: any) => {
        refresher.complete();
      },
      () => {
        refresher.complete();
      },
    );
  }

  /**
   * handles the search bar toggling and sliding the ion-content container down and up
   * @param {boolean} open is the searchbar open
   * @returns {void}
   */
  hSearchToggle(open: boolean): void {
    this.contentClass.searchBarOpen = open;
    this.contentClass.searchBarClosed = !open;
  }

  /**
   * Resets the items in the list and sets the page to 0
   * @param {ListEntity[]} items the list entities
   * @returns {void}
   * @protected
   */
  protected reset(items: any): void {
    this.page = 0;
    this.populateList(items);
    document.querySelector(this.listQuerySelector).parentElement.scrollTop = 0;
  }

  /**
   * append to the items array
   * @param {ListEntity[]} items the list entities
   * @returns {void}
   * @protected
   */
  protected append(items: ListEntity[]): void {
    this.items = this.items.concat(items);
    this.page++;
  }

  /**
   * Populates the list entities
   * @param {any} res the api response
   * @returns {void}
   * @protected
   */
  protected populateList(res: any): void {
    if (res.result) {
      this.items = this.service.populateListEntities(res.result.data);
      this.limit = res.result.paging.total;
      this.page++;
    }
  }

  /**
   * emits AuthError when service error is an auth error
   * @param {any} err Error object from the server
   * @returns {boolean}
   */
  protected handleServiceError(err: any): boolean {
    if ('AccessDeniedException' === err.error) {
      this.showLoginModal();
      return true;
    }
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/app/list/components/abstract/abstract-list.component.ts